// Marketing SEO: page meta, hreflang, JSON-LD and the static body used by the
// build-time prerender (build/prerender-seo.ts). Pure TS, no DOM/Vue imports:
// runs in the browser (useSeo) and in Node (vite.config).
import { FEATURES, getFeature, GROUP_LABELS, GROUPS, MK_LOCALES, relatedFeatures, visible, type MkLocale } from './features'
import { PACKS } from './i18n'
import { EN as UI_EN, PL as UI_PL } from './ui-copy'

// Prerender/SEO only sees unconditional items (capability-gated ones are revealed client-side).
const STATIC_FEATURES = FEATURES.filter((f) => visible(f))

export const SITE = {
  origin: 'https://sign.yt',
  name: 'sign.yt',
  salesEmail: 'sales@sign.yt',
  supportEmail: 'support@sign.yt',
  company: 'VARSOFIA',
  locales: MK_LOCALES,
  // Prerender constant; the app link uses VITE_DOCS_URL (lib/docsUrl.ts), same default.
  docs: 'https://docs.sign.yt',
}

/** /features/api-webhooks developer block (page + prerender). Package names: sdk/python, sdk/typescript. */
export const DEV_LINKS = [
  { key: 'dev_reference', href: `${SITE.docs}/api-reference` },
  { key: 'dev_quickstart', href: `${SITE.docs}/quickstart` },
  { key: 'dev_sdks', href: `${SITE.docs}/sdks/python` },
] as const
export const SDK_INSTALL = ['pip install signyt', 'npm install @signyt/sdk'] as const

export const OG_LOCALE: Record<MkLocale, string> = { en: 'en_US', pl: 'pl_PL', de: 'de_DE', es: 'es_ES', uk: 'uk_UA' }
/** Native language names (switcher, suggestion banner). */
export const LOCALE_NAMES: Record<MkLocale, string> = { en: 'English', pl: 'Polski', de: 'Deutsch', es: 'Español', uk: 'Українська' }

export type MkPage = 'home' | 'hub' | 'feature' | 'pricing'

const isMk = (s: string): s is MkLocale => (MK_LOCALES as readonly string[]).includes(s)

/** Locale prefix for a locale-less path: ('/features','pl') -> '/pl/features', ('/','de') -> '/de'. */
export function localePath(locale: MkLocale, path: string): string {
  if (locale === 'en') return path
  return path === '/' ? `/${locale}` : `/${locale}${path}`
}

/** Inverse of localePath: '/de/pricing' -> { locale: 'de', path: '/pricing' }. */
export function splitLocale(fullPath: string): { locale: MkLocale; path: string } {
  const m = /^\/([a-z]{2})(\/.*)?$/.exec(fullPath)
  if (m && m[1] !== 'en' && isMk(m[1])) return { locale: m[1], path: !m[2] || m[2] === '/' ? '/' : m[2] }
  return { locale: 'en', path: fullPath }
}

export type PlanId = 'starter' | 'professional' | 'business' | 'enterprise'
export interface Plan {
  id: PlanId
  name: string
  /** PLN net per month; null = custom quote. */
  price: number | null
  bullets: Record<MkLocale, string[]>
}

// Limits per PRD 4.10. The only place prices live (pages, meta, JSON-LD read from here).
const PLANS_ENPL: (Omit<Plan, 'bullets'> & { bullets: Record<'en' | 'pl', string[]> })[] = [
  {
    id: 'starter', name: 'Starter', price: 49,
    bullets: {
      en: ['50 documents / month', '3 users, 1 team', 'Email code verification', '"Powered by sign.yt" on login pages'],
      pl: ['50 dokumentów / mies.', '3 użytkowników, 1 zespół', 'Weryfikacja kodem e-mail', '"Powered by sign.yt" na stronach logowania'],
    },
  },
  {
    id: 'professional', name: 'Professional', price: 149,
    bullets: {
      en: ['500 documents / month', '10 users, 5 teams', '50 SMS codes / month', 'Custom domain, no watermark'],
      pl: ['500 dokumentów / mies.', '10 użytkowników, 5 zespołów', '50 kodów SMS / mies.', 'Własna domena, bez znaku wodnego'],
    },
  },
  {
    id: 'business', name: 'Business', price: 499,
    bullets: {
      en: ['Unlimited documents, users and teams', 'SSO', 'Your own email provider', 'REST API and webhooks'],
      pl: ['Bez limitu dokumentów, użytkowników i zespołów', 'SSO', 'Własny dostawca e-mail', 'REST API i webhooki'],
    },
  },
  {
    id: 'enterprise', name: 'Enterprise', price: null,
    bullets: {
      en: ['Everything in Business', 'SAML', 'SLA and dedicated support', 'On-premise option'],
      pl: ['Wszystko z planu Business', 'SAML', 'SLA i dedykowane wsparcie', 'Opcja on-premise'],
    },
  },
]
export const PLANS: Plan[] = PLANS_ENPL.map((p) => ({
  ...p,
  bullets: { ...p.bullets, de: PACKS.de.plans[p.id], es: PACKS.es.plans[p.id], uk: PACKS.uk.plans[p.id] },
}))
const PRICED = PLANS.filter((p): p is Plan & { price: number } => p.price !== null)
/** "Starter 49, Professional 149, Business 499" for copy with a {plans} placeholder. */
const PLAN_LIST = PRICED.map((p) => `${p.name} ${p.price}`).join(', ')

/** Comparison table row: label + one cell per plan (true/false = check/dash, string = value). */
export type PricingCell = boolean | string
export type PricingRow = [string, PricingCell[]]

const ROWS_ENPL: Record<'en' | 'pl', PricingRow[]> = {
  en: [
    ['Documents per month', ['50', '500', 'Unlimited', 'Unlimited']],
    ['Users', ['3', '10', 'Unlimited', 'Unlimited']],
    ['Teams', ['1', '5', 'Unlimited', 'Unlimited']],
    ['Email code verification', [true, true, true, true]],
    ['SMS codes', [false, '50 / month', true, true]],
    ['Custom signing domain', [false, true, true, true]],
    ['Your branding, no "Powered by"', [false, true, true, true]],
    ['SSO (Google, Microsoft, OIDC)', [false, false, true, true]],
    ['Your own email provider', [false, false, true, true]],
    ['REST API and webhooks', [false, false, true, true]],
    ['SAML', [false, false, false, true]],
    ['SLA and dedicated support', [false, false, false, true]],
    ['On-premise option', [false, false, false, true]],
  ],
  pl: [
    ['Dokumenty miesięcznie', ['50', '500', 'Bez limitu', 'Bez limitu']],
    ['Użytkownicy', ['3', '10', 'Bez limitu', 'Bez limitu']],
    ['Zespoły', ['1', '5', 'Bez limitu', 'Bez limitu']],
    ['Weryfikacja kodem e-mail', [true, true, true, true]],
    ['Kody SMS', [false, '50 / mies.', true, true]],
    ['Własna domena podpisu', [false, true, true, true]],
    ['Twój branding, bez "Powered by"', [false, true, true, true]],
    ['SSO (Google, Microsoft, OIDC)', [false, false, true, true]],
    ['Własny dostawca e-mail', [false, false, true, true]],
    ['REST API i webhooki', [false, false, true, true]],
    ['SAML', [false, false, false, true]],
    ['SLA i dedykowane wsparcie', [false, false, false, true]],
    ['Opcja on-premise', [false, false, false, true]],
  ],
}
export const PRICING_ROWS: Record<MkLocale, PricingRow[]> = {
  ...ROWS_ENPL, de: PACKS.de.pricingRows, es: PACKS.es.pricingRows, uk: PACKS.uk.pricingRows,
}

export interface Faq { q: string; a: string }

// Pricing FAQ: shown on /pricing, in its FAQPage JSON-LD and prerendered body.
export const PRICING_FAQ: Record<MkLocale, Faq[]> = {
  en: [
    { q: 'Are the prices net or gross?', a: 'All prices are monthly net prices in PLN. Polish VAT (23%) is added on the invoice.' },
    { q: 'What happens if we go over the document limit?', a: 'Paid plans keep working. Documents above the monthly limit are billed at 2 PLN each on your next invoice, and admins get an email alert at 80% and 100% of the limit.' },
    { q: 'Do you issue VAT invoices?', a: 'Yes. Every payment comes with a VAT invoice for your company, with your tax ID and billing address from the organization settings.' },
    { q: 'Can we change plans later?', a: 'Yes. You can switch plans at any time in the billing portal, available from your billing settings. Limits follow the new plan.' },
    { q: 'Can we add more teams without moving to a bigger plan?', a: 'Extra teams can be added to a paid plan for a monthly fee. Write to us and we will set it up for your organization.' },
    { q: 'How does Enterprise pricing work?', a: `Enterprise is quoted to your volume, security and hosting needs, including SAML, an SLA and an on-premise option. Contact ${SITE.salesEmail}.` },
  ],
  pl: [
    { q: 'Czy ceny są netto czy brutto?', a: 'Wszystkie ceny to miesięczne kwoty netto w złotych. Do faktury doliczamy VAT 23%.' },
    { q: 'Co się stanie, gdy przekroczymy limit dokumentów?', a: 'Płatne plany działają dalej. Dokumenty ponad miesięczny limit rozliczamy po 2 zł za sztukę na kolejnej fakturze, a administratorzy dostają e-mail przy 80% i 100% limitu.' },
    { q: 'Czy wystawiacie faktury VAT?', a: 'Tak. Do każdej płatności wystawiamy fakturę VAT na Twoją firmę, z NIP i adresem z ustawień organizacji.' },
    { q: 'Czy możemy później zmienić plan?', a: 'Tak. Plan zmienisz w dowolnym momencie w portalu płatności, dostępnym z ustawień rozliczeń. Limity dopasowują się do nowego planu.' },
    { q: 'Czy możemy dodać zespoły bez zmiany planu na wyższy?', a: 'Do płatnego planu można dokupić dodatkowe zespoły w opłacie miesięcznej. Napisz do nas, a skonfigurujemy to dla Twojej organizacji.' },
    { q: 'Jak wyceniacie plan Enterprise?', a: `Enterprise wyceniamy pod Twój wolumen, wymagania bezpieczeństwa i hostingu, w tym SAML, SLA i opcję on-premise. Napisz na ${SITE.salesEmail}.` },
  ],
  de: PACKS.de.pricingFaq,
  es: PACKS.es.pricingFaq,
  uk: PACKS.uk.pricingFaq,
}

/** title: without the " | sign.yt" suffix (home: full title). desc: may contain {plans}. */
export interface PageCopy { title: string; desc: string; h1: string; lead: string }

// Meta + prerender copy for non-feature pages. Home h1/lead mirror i18n home.hero_*;
// a landing CMS hero override changes only the rendered page, not this static copy.
const PAGES_ENPL: Record<Exclude<MkPage, 'feature'>, Record<'en' | 'pl', PageCopy>> = {
  home: {
    en: {
      title: 'sign.yt: e-signatures on your own domain',
      desc: 'Electronic signature platform for offices, coworkings and accounting firms: your own domain and branding, PAdES seal, SMS and email signer verification.',
      h1: 'Contracts signed under your own name.',
      lead: 'sign.yt runs the whole signing flow on your domain, with your logo and your email sender. Signers verify with an email or SMS code, and every PDF comes back PAdES-sealed with a full audit trail.',
    },
    pl: {
      title: 'sign.yt: podpis elektroniczny pod Twoją domeną',
      desc: 'Podpis elektroniczny dla biur wirtualnych, coworkingów i biur rachunkowych: własna domena i branding, pieczęć PAdES, weryfikacja podpisujących SMS i e-mail.',
      h1: 'Umowy podpisane pod Twoją marką.',
      lead: 'sign.yt prowadzi cały proces podpisu pod Twoją domeną, z Twoim logo i Twoim nadawcą e-maili. Sygnatariusze potwierdzają tożsamość kodem e-mail lub SMS, a każdy PDF wraca z pieczęcią PAdES i pełną ścieżką audytu.',
    },
  },
  hub: {
    en: {
      title: 'E-signature features: domains, PAdES, API',
      desc: 'Everything sign.yt does: custom domains, branding, PAdES, trusted timestamps, Bitcoin anchoring, post-quantum seal, QR verification, API and GDPR tools.',
      h1: 'Everything you need to get documents signed',
      lead: 'Each feature below exists in the product today. Pick one to see how it works.',
    },
    pl: {
      title: 'Funkcje e-podpisu: domeny, PAdES, API',
      desc: 'Wszystko, co robi sign.yt: własne domeny, branding, PAdES, znaczniki czasu, kotwica w Bitcoinie, pieczęć postkwantowa, weryfikacja QR, API i narzędzia RODO.',
      h1: 'Wszystko, czego potrzebujesz do podpisywania dokumentów',
      lead: 'Każda z poniższych funkcji działa w produkcie już dziś. Wybierz jedną, aby zobaczyć szczegóły.',
    },
  },
  pricing: {
    en: {
      title: 'E-signature pricing: plans in PLN',
      desc: 'Monthly e-signature plans in PLN: {plans}, Enterprise on request. Net prices plus VAT, change or cancel your plan anytime.',
      h1: 'Plans that grow with your offices',
      lead: 'Monthly net prices in PLN. Start small and move up when you need custom domains, SSO or the API.',
    },
    pl: {
      title: 'Cennik podpisu elektronicznego w PLN',
      desc: 'Plany podpisu elektronicznego w PLN: {plans}, Enterprise na zapytanie. Ceny netto plus VAT, zmiana planu w dowolnym momencie.',
      h1: 'Plany, które rosną razem z Twoimi biurami',
      lead: 'Miesięczne ceny netto w PLN. Zacznij od małego planu i przejdź wyżej, gdy potrzebujesz własnej domeny, SSO albo API.',
    },
  },
}
export const PAGES = Object.fromEntries((['home', 'hub', 'pricing'] as const).map((p) => [p, {
  ...PAGES_ENPL[p], de: PACKS.de.pages[p], es: PACKS.es.pages[p], uk: PACKS.uk.pages[p],
}])) as Record<Exclude<MkPage, 'feature'>, Record<MkLocale, PageCopy>>

const LABELS_EN = {
  home: 'Home', features: 'Features', pricing: 'Pricing', faq: 'Frequently asked questions', month: 'month',
  custom: 'Custom', contact: 'Contact sales', verify: 'Verify a document', all: 'All features',
  more: 'More features', language: 'Language', breadcrumb: 'Breadcrumb',
  not_found_title: 'Page not found', not_found_lead: 'The page you are looking for does not exist or has moved.',
  back_home: 'Back to home',
}
export type LabelKey = keyof typeof LABELS_EN
export const LABELS: Record<MkLocale, Record<LabelKey, string>> = {
  en: LABELS_EN,
  pl: {
    home: 'Strona główna', features: 'Funkcje', pricing: 'Cennik', faq: 'Najczęstsze pytania', month: 'mies.',
    custom: 'Indywidualnie', contact: 'Kontakt z działem sprzedaży', verify: 'Zweryfikuj dokument', all: 'Wszystkie funkcje',
    more: 'Inne funkcje', language: 'Język', breadcrumb: 'Ścieżka nawigacji',
    not_found_title: 'Nie znaleziono strony', not_found_lead: 'Strona, której szukasz, nie istnieje albo została przeniesiona.',
    back_home: 'Wróć na stronę główną',
  },
  de: PACKS.de.labels,
  es: PACKS.es.labels,
  uk: PACKS.uk.labels,
}

type JsonLd = Record<string, unknown>

export interface PageMeta {
  page: MkPage | 'legal' | 'notfound' | 'cms'
  locale: MkLocale
  /** Locale-less path, e.g. '/features/custom-domains'. */
  basePath: string
  /** Full path with locale prefix. */
  path: string
  /** Full <title> including the brand suffix. */
  title: string
  desc: string
  h1: string
  lead: string
  canonical: string
  /** hreflang -> absolute URL (every locale + x-default); empty for single-language pages. */
  alternates: Record<string, string>
  ogImage: string
  noindex?: boolean
  jsonld: JsonLd[]
}

const abs = (path: string) => SITE.origin + (path === '/' ? '/' : path)
const ORG_ID = `${SITE.origin}/#organization`
const SITE_ID = `${SITE.origin}/#website`
const APP_ID = `${SITE.origin}/#software`

/** Social card per page type and locale (public/og/<type>-<locale>.png, 1200x630). */
const ogImage = (type: 'home' | 'features' | 'pricing', locale: MkLocale) => abs(`/og/${type}-${locale}.png`)

function basePathOf(page: MkPage, slug?: string): string {
  switch (page) {
    case 'home': return '/'
    case 'hub': return '/features'
    case 'pricing': return '/pricing'
    case 'feature': return `/features/${slug}`
  }
}

function crumbs(locale: MkLocale, items: [string, string][]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map(([name, path], i) => ({
      '@type': 'ListItem', position: i + 1, name, item: abs(localePath(locale, path)),
    })),
  }
}

const faqPage = (items: Faq[]): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((x) => ({ '@type': 'Question', name: x.q, acceptedAnswer: { '@type': 'Answer', text: x.a } })),
})

const offers = (url: string) => PRICED.map((p) => ({
  '@type': 'Offer', name: p.name, price: p.price, priceCurrency: 'PLN', url, availability: 'https://schema.org/InStock',
}))

const fill = (s: string) => s.replace('{plans}', PLAN_LIST)

/** Meta for one marketing page, or null for an unknown feature slug. */
export function pageMeta(page: MkPage, locale: MkLocale, slug?: string): PageMeta | null {
  const L = LABELS[locale]
  let copy: PageCopy
  const jsonld: JsonLd[] = []
  if (page === 'feature') {
    const f = slug ? getFeature(slug) : undefined
    if (!f) return null
    copy = f.copy[locale]
    jsonld.push(
      crumbs(locale, [[L.home, '/'], [L.features, '/features'], [f.copy[locale].name, basePathOf(page, slug)]]),
      faqPage(f.copy[locale].faq),
    )
  } else {
    copy = PAGES[page][locale]
  }
  const basePath = basePathOf(page, slug)
  const path = localePath(locale, basePath)
  const url = abs(path)
  const title = page === 'home' ? copy.title : `${copy.title} | ${SITE.name}`
  const desc = fill(copy.desc)

  if (page === 'home') {
    jsonld.push(
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': ORG_ID,
        name: SITE.name,
        url: abs('/'),
        logo: abs('/favicon.svg'),
        email: SITE.salesEmail,
        contactPoint: [
          { '@type': 'ContactPoint', contactType: 'sales', email: SITE.salesEmail, availableLanguage: [...MK_LOCALES] },
          { '@type': 'ContactPoint', contactType: 'customer support', email: SITE.supportEmail, availableLanguage: [...MK_LOCALES] },
        ],
        parentOrganization: { '@type': 'Organization', name: SITE.company },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': SITE_ID,
        name: SITE.name,
        url: abs('/'),
        inLanguage: [...MK_LOCALES],
        publisher: { '@id': ORG_ID },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        '@id': APP_ID,
        name: SITE.name,
        applicationCategory: 'BusinessApplication',
        applicationSubCategory: 'Electronic signature',
        operatingSystem: 'Web',
        url,
        description: desc,
        inLanguage: locale,
        publisher: { '@id': ORG_ID },
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'PLN',
          lowPrice: Math.min(...PRICED.map((p) => p.price)),
          highPrice: Math.max(...PRICED.map((p) => p.price)),
          offerCount: PRICED.length,
          offers: offers(abs(localePath(locale, '/pricing'))),
        },
      },
    )
  } else if (page === 'hub') {
    jsonld.push(crumbs(locale, [[L.home, '/'], [L.features, '/features']]), {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: STATIC_FEATURES.map((f, i) => ({
        '@type': 'ListItem', position: i + 1, name: f.copy[locale].name,
        url: abs(localePath(locale, `/features/${f.slug}`)),
      })),
    })
  } else if (page === 'pricing') {
    jsonld.push(crumbs(locale, [[L.home, '/'], [L.pricing, '/pricing']]), {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: SITE.name,
      description: desc,
      brand: { '@type': 'Brand', name: SITE.name },
      offers: offers(url),
    }, faqPage(PRICING_FAQ[locale]))
  }
  jsonld.push({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': url,
    url,
    name: title,
    description: desc,
    inLanguage: locale,
    isPartOf: { '@id': SITE_ID },
  })

  const alternates: Record<string, string> = {}
  for (const l of MK_LOCALES) alternates[l] = abs(localePath(l, basePath))
  alternates['x-default'] = alternates.en
  return {
    page, locale, basePath, path, title, desc,
    h1: copy.h1,
    lead: copy.lead,
    canonical: url,
    alternates,
    ogImage: ogImage(page === 'home' ? 'home' : page === 'pricing' ? 'pricing' : 'features', locale),
    jsonld,
  }
}

// Public legal pages + the verifier, one URL per locale (/privacy, /pl/privacy, ...).
// Body text lives in locales/<l>.json (<doc>.*); h1 mirrors <doc>.title there.
export const LEGAL_NAMES = ['privacy', 'terms', 'dpa', 'subprocessors', 'verify'] as const
export type LegalName = (typeof LEGAL_NAMES)[number]
type LegalCopy = { title: string; desc: string; h1: string }

export const LEGAL_PAGES: Record<LegalName, Record<MkLocale, LegalCopy>> = {
  privacy: {
    en: { title: 'Privacy policy', h1: 'Privacy Policy', desc: 'How sign.yt and VARSOFIA process personal data of users and document signers: purposes, legal bases, retention, EU hosting and your rights under the GDPR.' },
    pl: { title: 'Polityka prywatności', h1: 'Polityka prywatności', desc: 'Jak sign.yt i VARSOFIA przetwarzają dane osobowe użytkowników i sygnatariuszy: cele, podstawy prawne, okresy przechowywania, hosting w UE i prawa z RODO.' },
    de: { title: 'Datenschutzerklärung', h1: 'Datenschutzerklärung', desc: 'Wie sign.yt und VARSOFIA personenbezogene Daten von Nutzern und Unterzeichnern verarbeiten: Zwecke, Rechtsgrundlagen, Speicherdauer, EU-Hosting, DSGVO-Rechte.' },
    es: { title: 'Política de privacidad', h1: 'Política de privacidad', desc: 'Cómo sign.yt y VARSOFIA tratan los datos personales de usuarios y firmantes: finalidades, bases legales, conservación, alojamiento en la UE y tus derechos RGPD.' },
    uk: { title: 'Політика конфіденційності', h1: 'Політика конфіденційності', desc: 'Як sign.yt і VARSOFIA обробляють персональні дані користувачів і підписантів: цілі, правові підстави, строки зберігання, хостинг у ЄС і ваші права за GDPR.' },
  },
  terms: {
    en: { title: 'Terms of service', h1: 'Terms of Service', desc: 'Terms of service for the sign.yt electronic signature platform: accounts, plans and billing, acceptable use, availability, liability and how to end it.' },
    pl: { title: 'Regulamin usługi', h1: 'Regulamin', desc: 'Regulamin platformy podpisu elektronicznego sign.yt: konta, plany i rozliczenia, dozwolone użycie, dostępność, odpowiedzialność i zakończenie umowy.' },
    de: { title: 'Nutzungsbedingungen', h1: 'Nutzungsbedingungen', desc: 'Nutzungsbedingungen der E-Signatur-Plattform sign.yt: Konten, Tarife und Abrechnung, zulässige Nutzung, Verfügbarkeit, Haftung und Beendigung des Vertrags.' },
    es: { title: 'Términos del servicio', h1: 'Términos del servicio', desc: 'Términos del servicio de la plataforma de firma electrónica sign.yt: cuentas, planes y facturación, uso aceptable, disponibilidad, responsabilidad y baja.' },
    uk: { title: 'Умови надання послуг', h1: 'Умови надання послуг', desc: 'Умови використання платформи електронного підпису sign.yt: облікові записи, тарифи й оплата, допустиме використання, доступність, відповідальність і припинення.' },
  },
  dpa: {
    en: { title: 'Data processing agreement (DPA)', h1: 'Data Processing Agreement', desc: 'The sign.yt data processing agreement under Art. 28 GDPR: processing scope, security measures, sub-processors, audits and deletion of data after termination.' },
    pl: { title: 'Umowa powierzenia przetwarzania (DPA)', h1: 'Umowa Powierzenia Przetwarzania (DPA)', desc: 'Umowa powierzenia przetwarzania danych sign.yt zgodna z art. 28 RODO: zakres przetwarzania, środki bezpieczeństwa, podprocesorzy, audyty i usunięcie danych.' },
    de: { title: 'Auftragsverarbeitungsvertrag (AVV)', h1: 'Auftragsverarbeitungsvertrag', desc: 'Der Auftragsverarbeitungsvertrag von sign.yt nach Art. 28 DSGVO: Umfang der Verarbeitung, Sicherheitsmaßnahmen, Unterauftragsverarbeiter, Audits und Löschung.' },
    es: { title: 'Acuerdo de tratamiento de datos (DPA)', h1: 'Acuerdo de Tratamiento de Datos', desc: 'El acuerdo de tratamiento de datos de sign.yt según el art. 28 del RGPD: alcance del tratamiento, medidas de seguridad, subencargados, auditorías y borrado.' },
    uk: { title: 'Угода про обробку даних (DPA)', h1: 'Угода про обробку даних', desc: 'Угода про обробку даних sign.yt відповідно до ст. 28 GDPR: обсяг обробки, заходи безпеки, субобробники, аудити та видалення даних після припинення договору.' },
  },
  subprocessors: {
    en: { title: 'Sub-processors', h1: 'Sub-processors', desc: 'Current list of sub-processors used by sign.yt to host, secure and deliver the e-signature service, with their purpose, location and the transfer safeguards.' },
    pl: { title: 'Podprocesorzy', h1: 'Podprocesorzy', desc: 'Aktualna lista podprocesorów, z których sign.yt korzysta przy hostingu, ochronie i dostarczaniu usługi e-podpisu, z celem, lokalizacją i zabezpieczeniami.' },
    de: { title: 'Unterauftragsverarbeiter', h1: 'Unterauftragsverarbeiter', desc: 'Aktuelle Liste der Unterauftragsverarbeiter, die sign.yt für Hosting, Sicherheit und Zustellung des E-Signatur-Dienstes nutzt, mit Zweck, Standort, Garantien.' },
    es: { title: 'Subencargados del tratamiento', h1: 'Subencargados del tratamiento', desc: 'Lista actual de subencargados que sign.yt utiliza para alojar, proteger y prestar el servicio de firma electrónica, con su finalidad, ubicación y garantías.' },
    uk: { title: 'Субобробники', h1: 'Субобробники', desc: 'Актуальний перелік субобробників, яких sign.yt залучає для хостингу, захисту та надання сервісу електронного підпису, з метою, місцем розташування й гарантіями.' },
  },
  verify: {
    en: { title: 'Verify a signed document', h1: 'Document Verifier', desc: 'Check that a PDF signed with sign.yt is authentic and unchanged: upload the file or enter the code from its QR label to see the signatures and the audit trail.' },
    pl: { title: 'Zweryfikuj podpisany dokument', h1: 'Weryfikator dokumentów', desc: 'Sprawdź, czy PDF podpisany w sign.yt jest autentyczny i niezmieniony: wgraj plik lub wpisz kod z etykiety QR, aby zobaczyć podpisy i ścieżkę audytu.' },
    de: { title: 'Signiertes Dokument prüfen', h1: 'Dokumenten-Verifizierer', desc: 'Prüfen Sie, ob ein mit sign.yt signiertes PDF echt und unverändert ist: Datei hochladen oder Code vom QR-Etikett eingeben und Signaturen samt Audit-Trail sehen.' },
    es: { title: 'Verificar un documento firmado', h1: 'Verificador de documentos', desc: 'Comprueba que un PDF firmado con sign.yt es auténtico y no se ha modificado: sube el archivo o introduce el código de su etiqueta QR y ver firmas y auditoría.' },
    uk: { title: 'Перевірити підписаний документ', h1: 'Верифікатор документів', desc: 'Перевірте, що PDF, підписаний у sign.yt, справжній і не змінений: завантажте файл або введіть код із QR-етикетки, щоб побачити підписи та журнал аудиту.' },
  },
}

/** Internal links inside legal texts (href="/dpa") -> the same page in this locale. */
export const localizeLegalLinks = (html: string, locale: string) =>
  (MK_LOCALES as readonly string[]).includes(locale)
    ? html.replace(/href="\/(privacy|terms|dpa|subprocessors|verify|rodo-request)"/g, (_, p: string) => `href="${localePath(locale as MkLocale, `/${p}`)}"`)
    : html

export const isLegal = (s: string): s is LegalName => (LEGAL_NAMES as readonly string[]).includes(s)

/** Legal page in one locale; every locale has its own text (locales/<l>.json), so full hreflang set. */
export function legalMeta(name: LegalName, locale: MkLocale, lead = ''): PageMeta {
  const p = LEGAL_PAGES[name][locale]
  const basePath = `/${name}`
  const path = localePath(locale, basePath)
  const url = abs(path)
  const alternates: Record<string, string> = {}
  for (const l of MK_LOCALES) alternates[l] = abs(localePath(l, basePath))
  alternates['x-default'] = alternates.en
  return {
    page: 'legal', locale, basePath, path,
    title: `${p.title} | ${SITE.name}`, desc: p.desc, h1: p.h1, lead,
    canonical: url, alternates, ogImage: ogImage('home', locale),
    jsonld: [{
      '@context': 'https://schema.org', '@type': 'WebPage', '@id': url, url,
      name: p.title, description: p.desc, inLanguage: locale, isPartOf: { '@id': SITE_ID },
    }],
  }
}

/** Per-locale 404 shell (served by nginx with status 404, never indexed). */
export function notFoundMeta(locale: MkLocale): PageMeta {
  const L = LABELS[locale]
  const path = localePath(locale, '/404')
  return {
    page: 'notfound', locale, basePath: '/404', path,
    title: notFoundTitle(locale), desc: L.not_found_lead, h1: L.not_found_title, lead: L.not_found_lead,
    canonical: '', alternates: {}, ogImage: ogImage('home', locale), noindex: true, jsonld: [],
  }
}

/** "Page not found | sign.yt" in the URL's locale (prerendered shell + client 404). */
export const notFoundTitle = (locale: MkLocale) => `${LABELS[locale].not_found_title} | ${SITE.name}`

/** Every marketing URL (home, hub, pricing, each always-on feature) x every locale. */
export function marketingRoutes(): PageMeta[] {
  const out: PageMeta[] = []
  for (const locale of SITE.locales) {
    for (const page of ['home', 'hub', 'pricing'] as const) out.push(pageMeta(page, locale)!)
    for (const f of STATIC_FEATURES) out.push(pageMeta('feature', locale, f.slug)!)
  }
  return out
}

/** Capability-gated feature pages x every locale: prerendered under /_gated/, nginx serves
 *  them only while the backend says the capability is on (site_render.route). */
export function gatedRoutes(): PageMeta[] {
  return SITE.locales.flatMap((locale) => FEATURES.filter((f) => f.requires).map((f) => pageMeta('feature', locale, f.slug)!))
}

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const paras = (body: string) =>
  body.split(/\n\s*\n/).map((p) => `<p class="mk-lead mt-4">${esc(p.trim())}</p>`).join('')

const faqHtml = (title: string, items: Faq[]) =>
  `<section class="mt-16"><h2 class="mk-h3">${esc(title)}</h2>${items.map((x) =>
    `<details class="mt-4"><summary class="font-semibold">${esc(x.q)}</summary><p class="mt-2">${esc(x.a)}</p></details>`).join('')}</section>`

/** Prerender hooks (nginx SSI, build/prerender-seo.ts): wrap the home hero copy, append to main. */
export interface BodySlots { intro?: (html: string) => string; extra?: string }

const UI = { en: UI_EN, pl: UI_PL, de: PACKS.de.ui, es: PACKS.es.ui, uk: PACKS.uk.ui }
const NET_NOTE: Record<MkLocale, string> = Object.fromEntries(MK_LOCALES.map((l) => [l, UI[l].net_note])) as Record<MkLocale, string>

/** Static semantic HTML for <div id="app"> before Vue mounts (crawlers, no-JS). */
export function renderBodyHtml(m: PageMeta, slots: BodySlots = {}): string {
  const L = LABELS[m.locale]
  const lp = (p: string) => esc(localePath(m.locale, p))
  let main = `<h1 class="mk-display mk-h1">${esc(m.h1)}</h1>${m.lead ? `<p class="mk-lead mt-6 max-w-3xl">${esc(m.lead)}</p>` : ''}`
  if (slots.intro) main = slots.intro(main)

  const featureList = (list: typeof FEATURES) => `<ul class="mt-10 grid gap-4 sm:grid-cols-2">${list.map((f) =>
    `<li class="mk-card p-6"><a class="font-semibold underline-offset-4 hover:underline" href="${lp(`/features/${f.slug}`)}">${esc(f.copy[m.locale].name)}</a><p class="mt-2 text-sm">${esc(f.copy[m.locale].desc)}</p></li>`).join('')}</ul>`
  const trail = (items: [string, string | null][]) => `<nav aria-label="${esc(L.breadcrumb)}" class="mb-8 text-sm">${items.map(([name, p]) =>
    p ? `<a href="${lp(p)}">${esc(name)}</a>` : `<span aria-current="page">${esc(name)}</span>`).join(' / ')}</nav>`

  if (m.page === 'home') {
    main += featureList(STATIC_FEATURES)
  } else if (m.page === 'hub') {
    main = trail([[L.home, '/'], [L.features, null]]) + main + GROUPS.map((g) => `<section class="mt-16"><h2 class="mk-h3">${esc(GROUP_LABELS[g][m.locale])}</h2>${
      featureList(STATIC_FEATURES.filter((f) => f.group === g))}</section>`).join('')
  } else if (m.page === 'pricing') {
    main = trail([[L.home, '/'], [L.pricing, null]]) + main
    main += `<ul class="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">${PLANS.map((p) =>
      `<li class="mk-card p-6"><h2 class="mk-h3">${esc(p.name)}</h2><p class="mt-2 font-semibold">${p.price === null ? esc(L.custom) : `${p.price} PLN / ${esc(L.month)}, ${esc(NET_NOTE[m.locale])}`}</p><ul class="mt-4 space-y-1 text-sm">${p.bullets[m.locale].map((b) => `<li>${esc(b)}</li>`).join('')}</ul></li>`).join('')}</ul>`
    main += faqHtml(L.faq, PRICING_FAQ[m.locale])
  } else if (m.page === 'feature') {
    const f = getFeature(m.basePath.split('/').pop()!)!
    const c = f.copy[m.locale]
    main = trail([[L.home, '/'], [L.features, '/features'], [c.name, null]]) + main + c.sections.filter((s) => visible(s)).map((s) =>
      `<section class="mt-16"><h2 class="mk-h3">${esc(s.h2)}</h2>${paras(s.body)}</section>`).join('')
    if (f.slug === 'api-webhooks') {
      const T = UI[m.locale]
      main += `<section class="mt-16"><h2 class="mk-h3">${esc(T.dev_title)}</h2><p class="mk-lead mt-4">${esc(T.dev_lead)}</p><ul class="mt-4">${
        DEV_LINKS.map((l) => `<li><a href="${esc(l.href)}">${esc(T[l.key])}</a></li>`).join('')}</ul>${
        SDK_INSTALL.map((cmd) => `<pre><code>${esc(cmd)}</code></pre>`).join('')}</section>`
    }
    main += faqHtml(L.faq, c.faq)
    main += `<section class="mt-16"><h2 class="mk-h3">${esc(L.more)}</h2>${featureList(relatedFeatures(f))}</section>`
    main += `<p class="mt-16"><a class="mk-btn mk-btn--secondary" href="${lp('/features')}">${esc(L.all)}</a></p>`
  } else if (m.page === 'notfound') {
    main += `<p class="mt-10"><a class="mk-btn" href="${lp('/')}">${esc(L.back_home)}</a> <a href="${lp('/features')}">${esc(L.all)}</a> <a href="${lp('/pricing')}">${esc(L.pricing)}</a></p>`
  }

  main += slots.extra ?? ''
  // Locale links only where every locale has the page.
  const langs = Object.keys(m.alternates).length
    ? `<nav aria-label="${esc(L.language)}" class="flex gap-3 text-sm">${MK_LOCALES.map((l) =>
      `<a href="${esc(m.alternates[l].slice(SITE.origin.length))}" hreflang="${l}" lang="${l}"${l === m.locale ? ' aria-current="true"' : ''}>${esc(LOCALE_NAMES[l])}</a>`).join('')}</nav>`
    : ''
  const header = `<header class="mk-container flex flex-wrap items-center gap-6 py-5"><a class="mk-display text-2xl" href="${lp('/')}">sign.yt</a><nav class="flex flex-wrap gap-5 text-sm"><a href="${lp('/features')}">${esc(L.features)}</a><a href="${lp('/pricing')}">${esc(L.pricing)}</a><a href="${lp('/verify')}">${esc(L.verify)}</a><a href="mailto:${SITE.salesEmail}">${esc(L.contact)}</a></nav>${langs}</header>`
  return `<div class="mk min-h-screen">${header}<main id="main" class="mk-container py-16 md:py-24">${main}</main></div>`
}

/** <script type="application/ld+json"> payload; '<' escaped so it can't close the tag. */
export function jsonLdString(data: JsonLd): string {
  return JSON.stringify(data).replace(/</g, '\\u003c')
}

/** Head tags for one page as [tag, attrs, text?]; shared with the build prerender. */
export function headTags(m: PageMeta): [tag: 'meta' | 'link' | 'script', attrs: Record<string, string>, text?: string][] {
  const t: ReturnType<typeof headTags> = [['meta', { name: 'description', content: m.desc }]]
  if (m.noindex) t.push(['meta', { name: 'robots', content: 'noindex' }])
  if (m.canonical) t.push(['link', { rel: 'canonical', href: m.canonical }])
  for (const [hreflang, href] of Object.entries(m.alternates)) t.push(['link', { rel: 'alternate', hreflang, href }])
  const og: [string, string][] = [
    ['og:type', 'website'],
    ['og:site_name', SITE.name],
    ['og:title', m.title],
    ['og:description', m.desc],
    ...(m.canonical ? [['og:url', m.canonical] as [string, string]] : []),
    ['og:image', m.ogImage],
    ['og:image:width', '1200'],
    ['og:image:height', '630'],
    ['og:image:alt', m.title],
    ['og:locale', OG_LOCALE[m.locale]],
    ...Object.keys(m.alternates)
      .filter((l) => l !== m.locale && l in OG_LOCALE)
      .map((l) => ['og:locale:alternate', OG_LOCALE[l as keyof typeof OG_LOCALE]] as [string, string]),
  ]
  for (const [property, content] of og) t.push(['meta', { property, content }])
  for (const [name, content] of [
    ['twitter:card', 'summary_large_image'],
    ['twitter:title', m.title],
    ['twitter:description', m.desc],
    ['twitter:image', m.ogImage],
  ]) t.push(['meta', { name, content }])
  for (const d of m.jsonld) t.push(['script', { type: 'application/ld+json' }, jsonLdString(d)])
  return t
}
